import type { TFunction } from "i18next";
import { formatPersonFullName } from "../../../utils/formatters";
import type { NurseResult, RosterMatchedOn } from "../hooks";
import { type FloatPoolMergeRow, unresolvedSameName } from "./floatPoolMerge";

const SUMMARY = "onboarding_load.summary";

/** What matched, lower-cased for the middle of a sentence where the language capitalizes nouns only at the start. */
const MATCHED_ON_INLINE_KEY: Record<RosterMatchedOn, string> = {
	email: "onboarding_load.float_pool.matched_on_inline.email",
	phone_number: "onboarding_load.float_pool.matched_on_inline.phone_number",
	name: "onboarding_load.float_pool.matched_on_inline.name",
};

const matchedOnInline = (matchedOn: RosterMatchedOn[], t: TFunction): string => matchedOn.map((field) => t(MATCHED_ON_INLINE_KEY[field])).join(", ");

function linkedLine(row: NurseResult, t: TFunction, lastNameFirst: boolean): string {
	// DEFAULT: a linked row always carries both parts (it matched by them); a blank keeps the type honest.
	const name = formatPersonFullName({ firstName: row.first_name ?? "", lastName: row.last_name ?? "" }, lastNameFirst);
	const match = row.float_pool_matches?.find((candidate) => candidate.kind === "linked");
	if (!match) {
		// DEFAULT: the row's own reason — a linked row loses its match only when the record could not be described
		// (logged on the server); that reason carries the "could not show the matched record" note.
		return t(`${SUMMARY}.linked_item_unrendered`, { name, reason: row.reason });
	}
	// The record's first department is its home before this upload added the facility.
	return t(`${SUMMARY}.linked_item`, { name, id: match.nurseId, facility: match.departments[0].name });
}

/** One line per pair, except a row that was not applied: one line naming every same-name record it found. */
function mergeLines(mergeRows: FloatPoolMergeRow[], t: TFunction): string[] {
	const lines: string[] = [];
	const listedUnresolved = new Set<NurseResult>();
	for (const row of mergeRows) {
		const sameName = unresolvedSameName(row.source);
		if (sameName) {
			if (listedUnresolved.has(row.source)) continue;
			listedUnresolved.add(row.source);
			const records = sameName.map((record) => `#${record.nurseId}`).join(", ");
			lines.push(t(`${SUMMARY}.merge_item_not_applied`, { name: row.uploadedName, count: sameName.length, records }));
			continue;
		}
		lines.push(
			t(`${SUMMARY}.merge_item`, {
				name: row.uploadedName,
				hereId: row.recordHere.nurseId,
				id: row.existing.nurseId,
				facility: row.existing.departments[0].name,
				matchedOn: matchedOnInline(row.matchedOn, t),
			}),
		);
	}
	return lines;
}

/**
 * The copyable summary's sections for rows whose person already had a record: those the upload added this
 * facility to, and the pairs still left to merge. Empty sections are left out.
 */
export function floatPoolLines(linked: NurseResult[], mergeRows: FloatPoolMergeRow[], t: TFunction, lastNameFirst: boolean): string[] {
	const lines: string[] = [];
	if (linked.length > 0) lines.push(t(`${SUMMARY}.linked_section`, { count: linked.length }), ...linked.map((row) => linkedLine(row, t, lastNameFirst)), "");
	if (mergeRows.length > 0) {
		const merges = mergeLines(mergeRows, t);
		lines.push(t(`${SUMMARY}.merge_section`, { count: merges.length }), ...merges, "");
	}
	return lines;
}
